import React, { useEffect, useMemo, useState } from "react";

import {
  DownloadOutlined,
  LeftOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import {
  Button, Modal, Spin, Table,
} from "antd";
import moment from "moment";
import { toast } from "react-toastify";

import useAbortController from "../../../../Common/hooks/useAbortController";
import { SUBMISSION_DETAILS_FETCH_DEFAULTS } from "../../../../SampleSubmission/MainPage/Submissions/SubmissionDetailsSamplesTable";
import ShippingLabelModal from "./ShippingLabelModal";

import { downloadFileFromS3WithPresigned } from "../../../../../utils/helpers";
import { convertUtcToLocal } from "../../../../Common/utils/dateUtils";
import {
  getPickupStatusBadgeClass,
  getPickupStatusDisplayLabel,
  getPickupTrackingIdsDisplay,
} from "../../../Common/utils";

import { getSubmittedSubmissionsInfo } from "../../../../../actions/sampleSubmission";
import { fetchPickupRequestDetail } from "../../../../../actions/supplyPickup";

import closeIcon from "../../../../../assets/images/OnboardingClose.svg";

import "../../../Common/RequestTable.css";
import "./PickupDetailsModal.css";

const SAMPLE_FIELD_KEYS = {
  product: ["product", "Product", "product_name", "sample_id"],
  sample_type: ["sample_type", "Sample Type", "sampleType"],
  item: ["item", "Item"],
  time: ["time", "Time"],
  best_by: ["best_by", "Best by", "best_by_date", "Best By"],
  expected_date: ["expected_date", "Expected Date", "expectedDate"],
  sample_size: ["sample_size", "Sample Size", "sampleSize"],
};

const getSampleFieldValue = (sample, keys) => {
  for (let i = 0; i < keys.length; i += 1) {
    const value = sample?.[keys[i]];
    if (value != null && value !== "") return value;
  }
  return null;
};

const formatSampleCell = (value) => {
  if (value == null || value === "") return "-";
  return String(value);
};

const formatSampleTests = (sample) => {
  const parts = (sample?.tests_list ?? [])
    .map((test) => (typeof test === "string" ? test : test?.Test))
    .filter(Boolean);
  return parts.length ? parts.join(", ") : "-";
};

const getSampleTypeBadgeClass = (value) => {
  const type = String(value || "").toLowerCase();
  if (type.includes("solid")) return "pickup-details-sample-type pickup-details-sample-type--solid";
  if (type.includes("liquid")) return "pickup-details-sample-type pickup-details-sample-type--liquid";
  if (type.includes("powder")) return "pickup-details-sample-type pickup-details-sample-type--powder";
  return "pickup-details-sample-type pickup-details-sample-type--default";
};

const LABELS_CATEGORIES = new Set(["fedex_pickup_labels", "fedex_dropoff_labels"]);

const getLabelS3Path = (label) => label?.label_s3_path || null;

const formatPickupWindow = (value) => {
  if (!value) return "-";
  const parts = String(value).split(/\s*-\s*/).map((t) => t.trim()).filter(Boolean);
  if (parts.length < 2) return value;
  const [start, end] = parts;
  const startTime = moment(start, ["HH:mm:ss", "HH:mm", "h:mm A"], true);
  const endTime = moment(end, ["HH:mm:ss", "HH:mm", "h:mm A"], true);
  if (!startTime.isValid() || !endTime.isValid()) return value;
  return `${startTime.format("h:mm A")} - ${endTime.format("h:mm A")}`;
};

const formatSubmittedOn = (submitDate) => {
  if (!submitDate) return "-";
  return moment(new Date(convertUtcToLocal(submitDate))).format("D MMM YYYY | hh:mm a");
};

const formatSampleCount = (count) => {
  const n = Number(count);
  if (!Number.isFinite(n) || n < 0) return "-";
  return String(n).padStart(2, "0");
};

const getTotalTests = (submission) => {
  const value = submission?.total_tests ?? submission?.num_tests ?? submission?.tests_count;
  if (value == null || value === "") {
    const fromSamples = (submission?.samples_list || []).reduce((sum, sample) => {
      const tests = sample?.tests_list;
      return sum + (Array.isArray(tests) ? tests.length : 0);
    }, 0);
    return fromSamples ? String(fromSamples) : "-";
  }
  return String(value);
};

const getSubmissionId = (entry) => {
  if (typeof entry === "string" || typeof entry === "number") return String(entry);
  return String(entry?.submission_id || entry?.id || "");
};

const PickupDetailsModal = ({
  onCancel,
  trackingId,
  packaging,
  pickupDate,
  // isFedexPickup = false,
  packageCount,
  totalWeight,
  weightUnit,
  pickupWindow,
  pickupOrderId,
  fedexLabels,
  pickupCategory,
  submissionList,
  status,
}) => {
  const [loading, setLoading] = useState(Boolean(pickupOrderId));
  const [detail, setDetail] = useState(null);
  const [hasFetchedDetail, setHasFetchedDetail] = useState(!pickupOrderId);
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [hasFetchedSubmissions, setHasFetchedSubmissions] = useState(false);
  const [labelModalOpen, setLabelModalOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const { setNewAbortController } = useAbortController();

  useEffect(() => {
    if (!pickupOrderId) {
      setLoading(false);
      setHasFetchedDetail(true);
      return undefined;
    }

    let cancelled = false;
    const fetchDetail = async () => {
      setLoading(true);
      setHasFetchedDetail(false);
      setHasFetchedSubmissions(false);
      setSubmissions([]);
      setSelectedSubmission(null);
      const newAbortController = setNewAbortController();
      const {
        success, result, message, canceled,
      } = await fetchPickupRequestDetail(pickupOrderId, newAbortController.signal);

      if (cancelled || newAbortController.signal.aborted) return;

      if (success) {
        setDetail(result || null);
      } else if (!canceled) {
        setDetail(null);
        toast.error(message);
      }
      setLoading(false);
      setHasFetchedDetail(true);
    };

    fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [pickupOrderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolvedSubmissionIds = useMemo(() => {
    const list = detail?.submission_list || submissionList || [];
    if (!Array.isArray(list)) return [];
    return list.map(getSubmissionId).filter(Boolean);
  }, [detail, submissionList]);

  const submissionIdsKey = resolvedSubmissionIds.join(",");

  useEffect(() => {
    if (!hasFetchedDetail) return undefined;

    if (!submissionIdsKey) {
      setSubmissions([]);
      setSubmissionsLoading(false);
      setHasFetchedSubmissions(true);
      return undefined;
    }

    const ids = submissionIdsKey.split(",");
    let cancelled = false;
    const fetchSubmissions = async () => {
      setSubmissionsLoading(true);
      setHasFetchedSubmissions(false);
      const results = await Promise.all(ids.map(async (submissionId) => {
        const { success, result } = await getSubmittedSubmissionsInfo({
          ...SUBMISSION_DETAILS_FETCH_DEFAULTS,
          submission_id: submissionId,
          sample_category: "",
        });
        if (!success || !result) return null;
        return {
          submission_id: result.submission_id || submissionId,
          submit_name: result.submit_name || "-",
          submit_date: result.submit_date || null,
          po: result.po || null,
          submitted_by: result.submitted_by || null,
          samples_list: Array.isArray(result.samples_list) ? result.samples_list : [],
          num_samples: Number(result.num_samples) || (result.samples_list || []).length || 0,
          total_tests: result.total_tests ?? result.num_tests ?? result.tests_count ?? null,
        };
      }));

      if (cancelled) return;
      setSubmissions(results.filter(Boolean));
      setSubmissionsLoading(false);
      setHasFetchedSubmissions(true);
    };

    fetchSubmissions();
    return () => {
      cancelled = true;
    };
  }, [hasFetchedDetail, submissionIdsKey]);

  const resolvedTrackingId = detail
    ? getPickupTrackingIdsDisplay(detail)
    : (trackingId || "-");
  const resolvedPackaging = detail ? (detail.boxes || []) : packaging;
  const resolvedFedexConfirmationNumber = detail?.fedex_confirmation_number || "-";
  const resolvedPickupDate = detail ? detail.pickup_date : pickupDate;
  const resolvedPackageCount = detail ? detail.package_count : packageCount;
  const resolvedTotalWeight = detail ? detail.total_weight : totalWeight;
  // detail.total_weight already carries its unit ("86 lb"); the props path does not.
  const resolvedWeightUnit = detail ? null : weightUnit;
  const resolvedPickupWindow = detail ? detail.pickup_window : pickupWindow;
  const resolvedCategory = detail?.pickup_category || pickupCategory;
  const resolvedLabels = detail?.fedex_labels || fedexLabels || [];
  const resolvedStatus = detail?.status || status || "";
  const resolvedPickupOrderId = detail?.pickup_order_id || pickupOrderId;

  const isContentLoading = Boolean(pickupOrderId) && (
    loading
    || !hasFetchedDetail
    || submissionsLoading
    || !hasFetchedSubmissions
  );

  const hasDownloadableLabels = !isContentLoading
    && LABELS_CATEGORIES.has(resolvedCategory)
    && resolvedLabels.some((label) => getLabelS3Path(label));
  const hasPrintableLabels = !isContentLoading
    && LABELS_CATEGORIES.has(resolvedCategory)
    && resolvedLabels.some(
      (label) => getLabelS3Path(label) || label?.label_base64,
    );
  const showLabelActions = !isContentLoading
    && String(resolvedStatus).toLowerCase() !== "cancelled"
    && LABELS_CATEGORIES.has(resolvedCategory);

  const handleDownloadLabels = async () => {
    const labelsWithPath = resolvedLabels.filter((label) => getLabelS3Path(label));
    if (!LABELS_CATEGORIES.has(resolvedCategory) || labelsWithPath.length === 0) {
      toast.error("No shipping label is available for this pickup.");
      return;
    }

    const paths = labelsWithPath.map((label) => getLabelS3Path(label));
    const fileNames = labelsWithPath.map(
      (label, index) => `shipping-label-${label.tracking_number || index + 1}.pdf`,
    );
    const success = await downloadFileFromS3WithPresigned(paths, "private", fileNames);
    if (!success) {
      toast.error("Unable to download shipping label.");
    }
  };

  const handlePrintLabels = () => {
    if (!hasPrintableLabels) {
      toast.error("No shipping label is available for this pickup.");
      return;
    }
    setLabelModalOpen(true);
  };

  const formattedDate = useMemo(() => {
    if (!resolvedPickupDate) return "-";
    return moment(resolvedPickupDate, ["YYYY-MM-DD", "MM/DD/YYYY"]).format("dddd, MMMM D, YYYY");
  }, [resolvedPickupDate]);

  const totalBoxesDisplay = useMemo(() => {
    let count = Number(resolvedPackageCount);
    if (!Number.isFinite(count) || count <= 0) {
      count = (resolvedPackaging || []).reduce(
        (sum, item) => sum + (Number(item?.quantity) || 0),
        0,
      );
    }
    if (!count) return "-";
    return String(count).padStart(2, "0");
  }, [resolvedPackageCount, resolvedPackaging]);

  const totalWeightDisplay = useMemo(() => {
    if (resolvedTotalWeight == null || resolvedTotalWeight === "") return "-";
    if (!resolvedWeightUnit) return String(resolvedTotalWeight);
    return `${resolvedTotalWeight} ${resolvedWeightUnit}`;
  }, [resolvedTotalWeight, resolvedWeightUnit]);

  const samplesCount = useMemo(() => {
    if (!submissions.length) {
      return resolvedSubmissionIds.length ? String(resolvedSubmissionIds.length) : "-";
    }
    const total = submissions.reduce((sum, item) => sum + (item.num_samples || 0), 0);
    return total ? String(total) : "-";
  }, [submissions, resolvedSubmissionIds]);

  const headerId = resolvedPickupOrderId || "—";
  const statusLabel = resolvedStatus
    ? getPickupStatusDisplayLabel(resolvedStatus, resolvedCategory)
    : "";
  const statusBadgeClass = resolvedStatus
    ? getPickupStatusBadgeClass(resolvedStatus, resolvedCategory)
    : "RequestTableStatusBadge--default";

  const summaryColumns = useMemo(() => ([
    {
      title: "Tracking ID",
      dataIndex: "tracking",
      key: "tracking",
    },
    {
      title: "Fedex Confirmation No.",
      dataIndex: "fedex_confirmation_number",
      key: "fedex_confirmation_number",
      render: (value) => value || "-",
    },
    {
      title: "Pickup Date",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Pickup Time",
      dataIndex: "time",
      key: "time",
    },
    {
      title: "Samples",
      dataIndex: "samples",
      key: "samples",
    },
    {
      title: "Total Weight",
      dataIndex: "weight",
      key: "weight",
    },
    {
      title: "Total Boxes",
      dataIndex: "boxes",
      key: "boxes",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => value,
    },
  ]), []);

  const summaryDataSource = useMemo(() => {
    if (isContentLoading) return [];
    return [{
      key: "summary",
      tracking: resolvedTrackingId,
      fedex_confirmation_number: resolvedFedexConfirmationNumber,
      date: formattedDate,
      time: formatPickupWindow(resolvedPickupWindow),
      samples: samplesCount,
      weight: totalWeightDisplay,
      boxes: totalBoxesDisplay,
      status: resolvedStatus ? (
        <span className={`RequestTableStatusBadge ${statusBadgeClass}`}>
          {statusLabel}
        </span>
      ) : "-",
    }];
  }, [
    isContentLoading,
    resolvedTrackingId,
    formattedDate,
    resolvedPickupWindow,
    resolvedFedexConfirmationNumber,
    samplesCount,
    totalWeightDisplay,
    totalBoxesDisplay,
    resolvedStatus,
    statusBadgeClass,
    statusLabel,
  ]);

  const submissionColumns = useMemo(() => ([
    {
      title: "Sample Submission",
      dataIndex: "submit_name",
      key: "submit_name",
      render: (value) => value || "-",
    },
    {
      title: "Submitted On",
      dataIndex: "submit_date",
      key: "submit_date",
      render: (value) => formatSubmittedOn(value),
    },
    {
      title: "PO Number",
      dataIndex: "po",
      key: "po",
      render: (value) => value || "-",
    },
    {
      title: "Submitted By",
      dataIndex: "submitted_by",
      key: "submitted_by",
      render: (value) => value || "-",
    },
    {
      title: "Samples",
      dataIndex: "num_samples",
      key: "num_samples",
      align: "center",
      className: "pickup-details-submissions-table__samples",
      render: (value) => formatSampleCount(value),
    },
    {
      title: "Total Tests",
      dataIndex: "total_tests",
      key: "total_tests",
      align: "center",
      render: (_, record) => getTotalTests(record),
    },
  ]), []);

  const selectedSamples = selectedSubmission?.samples_list || [];

  const renderSampleType = (sample) => {
    const value = getSampleFieldValue(sample, SAMPLE_FIELD_KEYS.sample_type);
    if (value == null || value === "") return "-";
    return (
      <span className={getSampleTypeBadgeClass(value)}>
        {String(value)}
      </span>
    );
  };

  const renderSamplesTable = () => {
    if (!selectedSamples.length) {
      return (
        <div className="pickup-details-samples-empty">
          No samples in this submission.
        </div>
      );
    }

    return (
      <div className="pickup-details-samples-tableWrap">
        <table className="pickup-details-samples-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Sample Type</th>
              <th className="pickup-details-samples-table__num">Item</th>
              <th className="pickup-details-samples-table__num">Time</th>
              <th>Best by</th>
              <th>Expected Date</th>
              <th className="pickup-details-samples-table__num">Sample Size</th>
              <th>Tests</th>
            </tr>
          </thead>
          <tbody>
            {selectedSamples.map((sample, index) => (
              <tr key={`${sample?.sample_id || sample?.uuid || "sample"}-${index}`}>
                <td>
                  {formatSampleCell(getSampleFieldValue(sample, SAMPLE_FIELD_KEYS.product))}
                </td>
                <td>{renderSampleType(sample)}</td>
                <td className="pickup-details-samples-table__num">
                  {formatSampleCell(getSampleFieldValue(sample, SAMPLE_FIELD_KEYS.item))}
                </td>
                <td className="pickup-details-samples-table__num">
                  {formatSampleCell(getSampleFieldValue(sample, SAMPLE_FIELD_KEYS.time))}
                </td>
                <td>
                  {formatSampleCell(getSampleFieldValue(sample, SAMPLE_FIELD_KEYS.best_by))}
                </td>
                <td>
                  {formatSampleCell(
                    getSampleFieldValue(sample, SAMPLE_FIELD_KEYS.expected_date),
                  )}
                </td>
                <td className="pickup-details-samples-table__num">
                  {formatSampleCell(
                    getSampleFieldValue(sample, SAMPLE_FIELD_KEYS.sample_size),
                  )}
                </td>
                <td>{formatSampleTests(sample)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTitle = !isContentLoading ? (
    <div className="pickup-details-title">
      <div className="pickup-details-title__accent" />
      <span className="pickup-details-title-text">{headerId}</span>
      {resolvedStatus && (
        <span className={`RequestTableStatusBadge ${statusBadgeClass}`}>
          {statusLabel}
        </span>
      )}
    </div>
  ) : null;

  return (
    <>
      <Modal
        centered
        title={renderTitle}
        className="pickup-details-modal"
        open
        onCancel={onCancel}
        footer={null}
        width={1100}
        closeIcon={<img src={closeIcon} alt="close icon" />}
        destroyOnClose
      >
        {isContentLoading ? (
          <div className="pickup-details-loading">
            <Spin size="large" />
          </div>
        ) : (
          <div className="pickup-details-body">
            <div className="pickup-details-content">
              <div className="pickup-details-table-card">
                <Table
                  className="pickup-details-summary-table"
                  columns={summaryColumns}
                  dataSource={summaryDataSource}
                  pagination={false}
                  rowKey="key"
                  size="middle"
                />
              </div>

              {selectedSubmission && (
                <button
                  type="button"
                  className="pickup-details-submission-back"
                  onClick={() => setSelectedSubmission(null)}
                >
                  <LeftOutlined aria-hidden className="pickup-details-submission-back__icon" />
                  <span className="pickup-details-submission-back__label">
                    {selectedSubmission.submit_name || "Submission"}
                  </span>
                </button>
              )}

              <div className="pickup-details-table-card">
                {selectedSubmission ? (
                  renderSamplesTable()
                ) : (
                  <Table
                    className="pickup-details-submissions-table"
                    columns={submissionColumns}
                    dataSource={submissions}
                    pagination={false}
                    rowKey="submission_id"
                    size="middle"
                    locale={{ emptyText: "No submissions linked." }}
                    onRow={(record) => ({
                      onClick: () => setSelectedSubmission(record),
                    })}
                  />
                )}
              </div>
            </div>

            {showLabelActions && (
              <div className="pickup-details-footer">
                <Button
                  className="pickup-details-footer__download"
                  icon={<DownloadOutlined aria-hidden />}
                  onClick={handleDownloadLabels}
                  disabled={!hasDownloadableLabels}
                >
                  Download Label
                </Button>
                <Button
                  type="primary"
                  className="pickup-details-footer__print"
                  icon={<PrinterOutlined aria-hidden />}
                  onClick={handlePrintLabels}
                  disabled={!hasPrintableLabels}
                >
                  Print Label
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
      {labelModalOpen && (
        <ShippingLabelModal
          open={labelModalOpen}
          labels={resolvedLabels}
          onCancel={() => setLabelModalOpen(false)}
        />
      )}
    </>
  );
};

export default PickupDetailsModal;
